// Minimal .xlsx reader — the counterpart to xlsx.ts, for the same reason: the portal ships no
// spreadsheet dependency. It reads cell text only (shared, inline and formula strings, numbers,
// booleans) from every sheet. Styles, dates-as-dates, merged cells and formulas are out of scope;
// a date comes back as Excel's serial number. Anything richer should pull in a real library.
//
// ZIP entries are read from the central directory; deflated ones are inflated with the
// platform's DecompressionStream('deflate-raw').

export interface ReadSheet {
  name: string;
  /** Dense rows of cell text; a missing cell is ''. */
  rows: string[][];
}

export class XlsxReadError extends Error {}

function u16(b: Uint8Array, o: number) { return b[o] | (b[o + 1] << 8); }
function u32(b: Uint8Array, o: number) { return (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0; }

interface Entry { name: string; method: number; size: number; offset: number }

function centralDirectory(b: Uint8Array): Map<string, Entry> {
  let eocd = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 22 - 65535); i--) {
    if (u32(b, i) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new XlsxReadError('Not an .xlsx file (no ZIP directory found).');
  const count = u16(b, eocd + 10);
  let p = u32(b, eocd + 16);
  const out = new Map<string, Entry>();
  const dec = new TextDecoder();
  for (let n = 0; n < count; n++) {
    if (u32(b, p) !== 0x02014b50) throw new XlsxReadError('Corrupt ZIP directory.');
    const method = u16(b, p + 10);
    const size = u32(b, p + 20);
    const nameLen = u16(b, p + 28);
    const extraLen = u16(b, p + 30);
    const commentLen = u16(b, p + 32);
    const offset = u32(b, p + 42);
    const name = dec.decode(b.subarray(p + 46, p + 46 + nameLen));
    out.set(name, { name, method, size, offset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

async function inflate(data: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') throw new XlsxReadError('This browser cannot read compressed .xlsx files.');
  const source = new ReadableStream<BufferSource>({ start(c) { c.enqueue(data as BufferSource); c.close(); } });
  const reader = source.pipeThrough(new DecompressionStream('deflate-raw')).getReader();
  const chunks: Uint8Array[] = [];
  for (let r = await reader.read(); !r.done; r = await reader.read()) chunks.push(r.value);
  const out = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0));
  let p = 0;
  for (const c of chunks) { out.set(c, p); p += c.length; }
  return out;
}

async function readEntry(b: Uint8Array, e: Entry): Promise<string> {
  if (u32(b, e.offset) !== 0x04034b50) throw new XlsxReadError(`Corrupt entry ${e.name}.`);
  const start = e.offset + 30 + u16(b, e.offset + 26) + u16(b, e.offset + 28);
  const raw = b.subarray(start, start + e.size);
  const bytes = e.method === 0 ? raw : e.method === 8 ? await inflate(raw) : null;
  if (!bytes) throw new XlsxReadError(`Unsupported compression in ${e.name}.`);
  return new TextDecoder().decode(bytes);
}

/** Every entry of a ZIP of text files, by path, in archive order. */
export async function readZip(data: ArrayBuffer | Uint8Array): Promise<Record<string, string>> {
  const b = data instanceof Uint8Array ? data : new Uint8Array(data);
  const out: Record<string, string> = {};
  for (const e of centralDirectory(b).values()) out[e.name] = await readEntry(b, e);
  return out;
}

const byLocal = (node: Document | Element, local: string) => Array.from(node.getElementsByTagNameNS('*', local));
const text = (el: Element) => byLocal(el, 't').map((t) => t.textContent ?? '').join('');

function colIndex(ref: string): number {
  let n = 0;
  for (const ch of ref.replace(/\d+$/, '')) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

export async function readXlsx(data: ArrayBuffer | Uint8Array): Promise<ReadSheet[]> {
  const b = data instanceof Uint8Array ? data : new Uint8Array(data);
  const dir = centralDirectory(b);
  const parse = (xml: string) => new DOMParser().parseFromString(xml, 'application/xml');
  const get = async (name: string) => {
    const e = dir.get(name);
    return e ? readEntry(b, e) : null;
  };

  const workbook = await get('xl/workbook.xml');
  if (!workbook) throw new XlsxReadError('Not an Excel workbook (no xl/workbook.xml).');
  const rels = new Map<string, string>();
  const relsXml = await get('xl/_rels/workbook.xml.rels');
  if (relsXml) for (const r of byLocal(parse(relsXml), 'Relationship')) rels.set(r.getAttribute('Id') ?? '', r.getAttribute('Target') ?? '');

  const shared: string[] = [];
  const ssXml = await get('xl/sharedStrings.xml');
  if (ssXml) for (const si of byLocal(parse(ssXml), 'si')) shared.push(text(si));

  const sheets: ReadSheet[] = [];
  for (const s of byLocal(parse(workbook), 'sheet')) {
    const rid = s.getAttribute('r:id') ?? Array.from(s.attributes).find((a) => a.localName === 'id')?.value ?? '';
    let target = rels.get(rid) ?? '';
    target = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
    const xml = await get(target);
    if (!xml) continue;
    const rows: string[][] = [];
    for (const row of byLocal(parse(xml), 'row')) {
      const r = Number(row.getAttribute('r') ?? rows.length + 1) - 1;
      const cells: string[] = [];
      let next = 0;
      for (const c of byLocal(row, 'c')) {
        const ref = c.getAttribute('r');
        const i = ref ? colIndex(ref) : next;
        next = i + 1;
        const t = c.getAttribute('t');
        const v = byLocal(c, 'v')[0]?.textContent ?? '';
        let value: string;
        if (t === 's') value = shared[Number(v)] ?? '';
        else if (t === 'inlineStr') value = text(c);
        else if (t === 'b') value = v === '1' ? 'TRUE' : 'FALSE';
        else value = v;
        while (cells.length < i) cells.push('');
        cells[i] = value;
      }
      while (rows.length < r) rows.push([]);
      rows[r] = cells;
    }
    sheets.push({ name: s.getAttribute('name') ?? `Sheet${sheets.length + 1}`, rows });
  }
  return sheets;
}
